/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 添加批量编辑照片页面
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-04 19:20:31
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Layout, message, Button, Popover } from 'antd'
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  setFilter,
  setQueryFilter,
  fetchPhotos,
  resetFilter,
  setLastPageCond
} from '../../../actions/photo'
import {
  appendPhotoPickerPhotos,
  setPhotoPickerPhotos
} from '../../../actions/photoPicker'
import { reset as resetPhotosByLocation } from '../../../slices/photosByLocationSlice'
import { reset as resetPhotosByPrecise } from '../../../slices/photosByPreciseSlice'
import { reset as resetAllPhotosSlice } from '../../../slices/allPhotoSlice'
import {
  buildFetchPhotosParams,
  buildFetchPhotosParamsWithOptions
} from '@/pages/components/photoLib/photo'
import { capturePromiseException } from '../../../utils/sentryUtils'
import actionTypes from '../../../constants/actionTypes'
import { setLoading } from '../../../actions/main'
import { setIsAscSort as setContentIsAscSort } from '../../../slices/photosByContentSlice'
import { setIsAscSort as setPreciseIsAscSort } from '../../../slices/photosByPreciseSlice'

import Filter from './components/Filter'
import HeaderExtra from './components/HeaderExtra'
import PhotosList from './components/PhotosList'
import { useAddPhotosTrack } from './hooks/useAddPhotosTrack'
import { formatDate } from './hooks/useAddPhotosTrack'
import dayjs from 'dayjs'
import './index.scss'
import type { PhotoItem } from '../batchEditor/context/WatermarkBatchContext'

import PageHeader from '@/components/PageHeader'
import AntdProvider from '@/components/AntdProvider'
const { Content } = Layout

interface AddBatchEditPhotosProps {
  inModal?: boolean
  onClose?: () => void
  currentPhoto?: PhotoItem | null
}

const AddBatchEditPhotos: React.FC<AddBatchEditPhotosProps> = ({
  inModal = false,
  onClose,
  currentPhoto
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const currentWatermarkKey = useMemo(() => {
    if (!currentPhoto) return null
    const baseID =
      (currentPhoto as any).watermarkBaseID ||
      (currentPhoto.watermarkID ? currentPhoto.watermarkID.split('+')[0] : '')
    const groupID =
      (currentPhoto as any).groupWatermarkID ||
      (currentPhoto.watermarkID && currentPhoto.watermarkID.includes('+')
        ? currentPhoto.watermarkID.split('+')[1]
        : '')

    if (!baseID && !groupID && currentPhoto.watermarkID) {
      return currentPhoto.watermarkID
    }
    if (!baseID && !groupID) return null
    if (baseID && groupID) return `${baseID}+${groupID}`
    return baseID || currentPhoto.watermarkID || null
  }, [currentPhoto])

  // Redux selectors
  const filter = useAppSelector((state) => state.photo.filter)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const photos = useAppSelector((state) => state.photo.photos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const groupId = useAppSelector((state) => state.group.groupId)

  const sameWatermarkOnly = useMemo(() => {
    if (!currentWatermarkKey) return false
    const watermarks = filter?.watermarks
    if (!watermarks || watermarks.length !== 1) return false
    return watermarks[0] === currentWatermarkKey
  }, [filter, currentWatermarkKey])

  const prevWatermarksRef = useRef<any>(null)

  // 使用埋点 hook
  const { trackAddPhotos } = useAddPhotosTrack()

  const [popoverVisible, setPopoverVisible] = useState(false)
  const backButtonRef = React.useRef<HTMLSpanElement>(null)

  // 提示关闭状态（从缓存读取）
  const [showFilterTip, setShowFilterTip] = useState(true)
  const FILTER_TIP_CLOSED_KEY = 'add-batch-filter-tip-closed'

  // 从缓存读取提示关闭状态
  useEffect(() => {
    const isClosed = localStorage.getItem(FILTER_TIP_CLOSED_KEY) === 'true'
    setShowFilterTip(!isClosed)
  }, [])

  // 关闭提示
  const handleCloseTip = () => {
    localStorage.setItem(FILTER_TIP_CLOSED_KEY, 'true')
    setShowFilterTip(false)
  }

  const selectedPhotosFromPicker = useAppSelector((state) => {
    // 检查photoPicker中的from字段是否为batch-editor
    if (state.photoPicker.from === 'batch-editor') {
      return state.photoPicker.photos
    }

    // 否则返回空数组
    return []
  })

  const handleFetchPhotos = useCallback(
    (isAscSort: boolean, overrideFilter?: any) => {
      // 清空选中状态
      dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
      dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
      dispatch(resetPhotosByLocation())
      dispatch(resetPhotosByPrecise())

      const finalIsAscSort = isAscSort || false

      dispatch(setContentIsAscSort(finalIsAscSort))
      dispatch(setPreciseIsAscSort(finalIsAscSort))

      dispatch(setLoading(true))

      const promises: Promise<any>[] = []

      promises[0] = dispatch(
        fetchPhotos(
          buildFetchPhotosParamsWithOptions({
            queryFilter: overrideFilter || queryFilter || filter,
            groupID: groupId,
            isAscSort: finalIsAscSort,
            lastPageCond: ''
          })
        )
      )
        .then((result: any) => {
          if (result.payload && result.payload.expandInfo) {
            dispatch({
              type: actionTypes.SET_PHOTOS_EXPAND_INFO,
              data: result.payload.expandInfo
            })
          }
          return result
        })
        .catch(capturePromiseException)

      Promise.all(promises).then(() => {
        dispatch(setLoading(false))
      })
    },
    [dispatch, filter, groupId, queryFilter]
  )

  const triggerFilter = useCallback(
    (newFilter: any) => {
      dispatch(setLastPageCond(''))

      const finalFilter = {
        ...newFilter,
        suggestedSearchWords:
          newFilter.searchKeywords?.length !== 1
            ? []
            : newFilter.suggestedSearchWords,
        useSegment: true
      }

      dispatch(setFilter(finalFilter))
      dispatch(setContentIsAscSort(false))
      dispatch(setPreciseIsAscSort(false))

      dispatch(setQueryFilter(finalFilter)).then(() => {
        handleFetchPhotos(false, finalFilter)
      })
    },
    [dispatch, handleFetchPhotos]
  )

  const handleToggleSameWatermark = useCallback(
    (checked: boolean) => {
      if (!currentWatermarkKey) {
        return
      }

      if (checked) {
        prevWatermarksRef.current = filter.watermarks
        const newFilter = {
          ...filter,
          watermarks: [currentWatermarkKey]
        }
        triggerFilter(newFilter)
      } else {
        const restoreList = prevWatermarksRef.current || []
        const newFilter = { ...filter, watermarks: restoreList }
        triggerFilter(newFilter)
        prevWatermarksRef.current = null
      }
    },
    [currentWatermarkKey, filter, triggerFilter]
  )

  // 检查照片是否有防伪码（用于 hasSelectedPhotos 计算）
  const hasAntiFakeCodeForMemo = useCallback((photo: any): boolean => {
    try {
      const extension = photo.extension
      if (!extension) {
        return false
      }
      // extension 可能是字符串或对象
      const extensionObj =
        typeof extension === 'string' ? JSON.parse(extension) : extension
      const antiFakeCode = extensionObj?.antiFakeCode
      return antiFakeCode && antiFakeCode.trim().length > 0
    } catch (error) {
      console.error('解析 extension 失败:', error)
      return false
    }
  }, [])

  // 计算是否有选中的照片（当前页面勾选但未添加的照片）
  const hasSelectedPhotos = useMemo(() => {
    // 如果 checkAll 为 true，检查是否有未取消勾选的照片
    if (checkAll) {
      const selectedSmallURLs = new Set(
        selectedPhotosFromPicker.map((p: any) => p.smallURL)
      )

      const filteredPhotos = photos.filter((p: any) => {
        const hasWatermark = p.hasWatermark === true
        const isImage = p.mediaType === 0
        const notSelected = !selectedSmallURLs.has(p.smallURL)
        const photoWatermarkBaseID = p.watermarkBaseID
        const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'
        const hasAntiFake = hasAntiFakeCodeForMemo(p)
        return (
          hasWatermark &&
          isImage &&
          notSelected &&
          isNotWatermarkBaseID21 &&
          hasAntiFake
        )
      })

      const unCheckedSeqSet = new Set(Object.keys(unCheckedPhotos).map(Number))
      // 如果有任何过滤后的照片不在 unCheckedPhotos 中，说明有选中的
      return filteredPhotos.some((p: any) => !unCheckedSeqSet.has(p.seq))
    } else {
      // 如果 checkAll 为 false，检查 checkedPhotos 中是否有照片
      const selectedSmallURLs = new Set(
        selectedPhotosFromPicker.map((p: any) => p.smallURL)
      )

      const filteredPhotos = photos.filter((p: any) => {
        const hasWatermark = p.hasWatermark === true
        const isImage = p.mediaType === 0
        const notSelected = !selectedSmallURLs.has(p.smallURL)
        const photoWatermarkBaseID = p.watermarkBaseID
        const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'
        const hasAntiFake = hasAntiFakeCodeForMemo(p)
        return (
          hasWatermark &&
          isImage &&
          notSelected &&
          isNotWatermarkBaseID21 &&
          hasAntiFake
        )
      })

      const checkedSeqSet = new Set(Object.keys(checkedPhotos).map(Number))
      // 检查 checkedPhotos 中是否有过滤后的照片
      return filteredPhotos.some((p: any) => checkedSeqSet.has(p.seq))
    }
  }, [
    photos,
    checkAll,
    checkedPhotos,
    unCheckedPhotos,
    selectedPhotosFromPicker,
    hasAntiFakeCodeForMemo
  ])

  // Initialization
  useEffect(() => {
    let initFilter = filter

    if (currentWatermarkKey) {
      const currentWatermarks = filter.watermarks || []
      const alreadySame =
        currentWatermarks.length === 1 &&
        currentWatermarks[0] === currentWatermarkKey

      if (!alreadySame) {
        prevWatermarksRef.current = currentWatermarks
      }

      initFilter = {
        ...filter,
        watermarks: [currentWatermarkKey]
      }
      dispatch(setFilter(initFilter))
    }

    // 初始化清除选中状态
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
    dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })

    // 埋点：进入页面
    const startTime = initFilter.times?.[0]
    const endTime = initFilter.times?.[1]
    const locations = initFilter.locations
    const watermarks = initFilter.watermarks
    const searchKeywords = initFilter.searchKeywords

    // 确保 watermarks 和 searchKeywords 正确获取和上报
    const watermarkList = watermarks && watermarks.length > 0 ? watermarks : []
    const searchWordValue =
      searchKeywords && searchKeywords.length > 0 ? searchKeywords[0] || '' : ''

    trackAddPhotos({
      actionType: 'enterPage',
      start_date: formatDate(startTime ? dayjs(startTime) : null),
      end_date: formatDate(endTime ? dayjs(endTime) : null),
      locationName:
        locations && locations.length > 0
          ? locations[0]?.name || '不限'
          : '不限',
      show_watermark_list: watermarkList,
      searchWord: searchWordValue
    })

    const currentGroupId = localStorage.getItem('group_id')

    if (currentGroupId) {
      // 使用默认筛选条件查询
      dispatch(setQueryFilter(initFilter))
      dispatch(
        fetchPhotos(buildFetchPhotosParams(initFilter, currentGroupId, ''))
      ).catch(capturePromiseException)
    } else {
      navigate('/choose_group', { replace: true })
    }

    // Cleanup
    return () => {
      dispatch(resetFilter())
      dispatch({ type: actionTypes.SET_PHOTOS, data: [] })
      dispatch({ type: actionTypes.SET_PHOTOS_NUM, data: 0 })
      dispatch(resetPhotosByLocation())
      dispatch(resetPhotosByPrecise())
      dispatch(resetAllPhotosSlice())
    }
  }, [])

  const handleBack = () => {
    // 如果有选中的照片（当前页面勾选但未添加的照片），显示确认弹窗
    // 检查条件：
    // 1. checkAll 为 true 时，检查是否有未取消勾选的照片
    // 2. checkAll 为 false 时，检查 checkedPhotos 中是否有照片
    if (hasSelectedPhotos) {
      setPopoverVisible(true)
      return
    }

    // 直接返回
    doBack()
  }

  const doBack = () => {
    // 埋点：返回
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'back',
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    if (inModal) {
      if (onClose) {
        onClose()
        return
      }
      navigate('/watermark/batchEditor')
      return
    }
    navigate('/watermark/batchEditor')
  }

  const handleCancelBack = () => {
    setPopoverVisible(false)
  }

  const handleConfirmBack = () => {
    setPopoverVisible(false)
    doBack()
  }

  // Popover 内容
  const popoverContent = (
    <div className="back-confirm-popover">
      <div className="back-confirm-popover__content">
        <ExclamationCircleOutlined className="back-confirm-popover__icon" />
        <div className="back-confirm-popover__text">
          <div>确认返回吗,照片还未添加</div>
          <div>
            如需添加请点击右上角{'"'}完成{'"'}
          </div>
        </div>
      </div>
      <div className="back-confirm-popover__buttons">
        <Button
          onClick={handleCancelBack}
          className="back-confirm-popover__cancel"
        >
          取消
        </Button>
        <Button
          type="primary"
          onClick={handleConfirmBack}
          className="back-confirm-popover__confirm"
        >
          返回
        </Button>
      </div>
    </div>
  )

  const handleConfirm = () => {
    // 创建已选中照片的 smallURL Set，用于快速查找
    const selectedSmallURLs = new Set(
      selectedPhotosFromPicker.map((p: any) => p.smallURL)
    )

    // 检查照片是否有防伪码
    const hasAntiFakeCode = (photo: any): boolean => {
      try {
        const extension = photo.extension
        if (!extension) {
          return false
        }
        // extension 可能是字符串或对象
        const extensionObj =
          typeof extension === 'string' ? JSON.parse(extension) : extension
        const antiFakeCode = extensionObj?.antiFakeCode
        return antiFakeCode && antiFakeCode.trim().length > 0
      } catch (error) {
        console.error('解析 extension 失败:', error)
        return false
      }
    }

    // 先过滤出符合筛选条件的照片（hasWatermark = true && mediaType = 0），并排除已选中的照片，同时排除 watermarkBaseID = "21" 的照片，以及没有防伪码的照片
    const filteredPhotos = photos.filter((p: any) => {
      // 基础筛选条件
      const hasWatermark = p.hasWatermark === true
      const isImage = p.mediaType === 0
      const notSelected = !selectedSmallURLs.has(p.smallURL)

      // 排除 watermarkBaseID = "21" 的照片
      const photoWatermarkBaseID = p.watermarkBaseID
      const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'

      // 检查是否有防伪码
      const hasAntiFake = hasAntiFakeCode(p)

      return (
        hasWatermark &&
        isImage &&
        notSelected &&
        isNotWatermarkBaseID21 &&
        hasAntiFake
      )
    })

    let selectedPhotosData: any[] = []

    if (checkAll) {
      const unCheckedSeqSet = new Set(Object.keys(unCheckedPhotos).map(Number))
      // 只从筛选后的照片中选择
      selectedPhotosData = filteredPhotos.filter(
        (p: any) => !unCheckedSeqSet.has(p.seq)
      )
    } else {
      const checkedSeqSet = new Set(Object.keys(checkedPhotos).map(Number))
      // 只从筛选后的照片中选择
      selectedPhotosData = filteredPhotos.filter((p: any) =>
        checkedSeqSet.has(p.seq)
      )
    }

    if (selectedPhotosData.length === 0) {
      message.warning('请至少选择一张照片')
      return
    }

    // 埋点：点击完成
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'complete',
      photoNum: selectedPhotosData.length,
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    // 如果 photoPicker 中已有 batch-editor 的数据，则追加；否则覆盖
    if (selectedPhotosFromPicker.length > 0) {
      // 追加选中的照片时，判断是否已存在相同 seq 的照片（使用 seq 作为唯一标识更可靠）
      const existingSeqSet = new Set(selectedPhotosFromPicker.map((p) => p.seq))
      const filteredSelectedPhotos = selectedPhotosData.filter(
        (p) => !existingSeqSet.has(p.seq)
      )

      if (filteredSelectedPhotos.length === 0) {
        message.warning('已存在相同的照片，请重新选择')
        return
      }

      if (filteredSelectedPhotos.length !== selectedPhotosData.length) {
        message.warning(
          `已移除 ${
            selectedPhotosData.length - filteredSelectedPhotos.length
          } 张重复照片`
        )
      }

      dispatch(appendPhotoPickerPhotos(filteredSelectedPhotos))
    } else {
      dispatch(setPhotoPickerPhotos(selectedPhotosData, 'batch-editor'))
    }
    if (inModal) {
      if (onClose) {
        onClose()
        return
      }
      navigate('/watermark/batchEditor')
      return
    }
    navigate('/watermark/batchEditor')
  }

  return (
    <Layout
      className={`Photo add-batch-photos-wrapper${inModal ? ' is-modal' : ''}`}
    >
      <PageHeader
        ghost={false}
        title={
          <div className="add-batch-photos-header-title">
            <Popover
              content={popoverContent}
              open={popoverVisible}
              onOpenChange={(open) => {
                setPopoverVisible(open)
              }}
              placement="bottomLeft"
              trigger="click"
              classNames={{ root: 'back-confirm-popover-wrapper' }}
              getPopupContainer={(triggerNode) =>
                triggerNode.parentElement || document.body
              }
            >
              <span
                ref={backButtonRef}
                className="add-batch-photos-back-btn"
                onClick={handleBack}
              >
                <LeftOutlined />
              </span>
            </Popover>
            <span className="add-batch-photos-title-text">
              添加批量编辑照片
            </span>
          </div>
        }
        className="add-batch-photos-header"
        extra={[
          <Button key="confirm" type="primary" onClick={handleConfirm}>
            完成
          </Button>
        ]}
      />

      <Content className="content add-batch-photos-content">
        <Filter sameWatermarkOnly={sameWatermarkOnly} />
        {/* 自动过滤提示 */}
        {showFilterTip && (
          <div className="add-batch-filter-tip">
            <InfoCircleOutlined className="add-batch-filter-tip__icon" />
            <span className="add-batch-filter-tip__text">
              已自动过滤：视频、无水印照片、已选择照片、考勤水印照片等类型
            </span>
            <CloseOutlined
              className="add-batch-filter-tip__close"
              onClick={handleCloseTip}
            />
          </div>
        )}
        <HeaderExtra
          sameWatermarkOnly={sameWatermarkOnly}
          currentWatermarkKey={currentWatermarkKey}
          onToggleSameWatermark={handleToggleSameWatermark}
        />

        {/* 包裹 PhotosList 的容器，确保 flex 布局生效 */}
        <div className="add-batch-photos-list-section">
          <PhotosList inModal={inModal} />
        </div>
      </Content>
    </Layout>
  )
}

const AddBatchEditPhotosPage = () => (
  <AntdProvider>
    <AddBatchEditPhotos />
  </AntdProvider>
)

// 同时导出内部组件供 Modal 使用
export { AddBatchEditPhotos }
export default AddBatchEditPhotosPage
